/* HeroKpi — Phase C1 (first visible feature improvement).
 *
 * One-glance answer to the dashboard's single most-asked question:
 *   "Am I on track to hit the FY target?"
 *
 * Today the same information is scattered — a sticky stat that only
 * appears on scroll (StickyHeaderStat), an attainment column in the
 * forecast table, a status pill in the head. This card consolidates
 * them into a permanent surface placed above the existing KpiStrip,
 * so the answer is the first thing you see when you open the page.
 *
 * Data is computed in ForecastItaly's component scope (lines ~1142-1161)
 * and passed in as props — no recomputation here, just presentation.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────┐
 *   │ [Target label]                       [Status pill]  │
 *   │                                                       │
 *   │  €{current}  →  €{target}                            │
 *   │                                                       │
 *   │  [━━━━━━━━░░░░░░░░░░░░░░] {pct}%                     │
 *   │                                                       │
 *   │  {gap text} · {pace text}                            │
 *   │  Last closed: {month} · {realized}                   │
 *   └─────────────────────────────────────────────────────┘
 *
 * The status pill (on/ahead/behind) reuses the same trackStatus value
 * already wired into StickyHeaderStat, so the two surfaces always agree.
 */
import React from "react";
import Card from "../ui/Card";

export type TrackStatus = "on" | "ahead" | "behind";

interface LastClosedLike {
  key: string;
  realEom: number;
}

interface HeroKpiProps {
  /** Current MRR (last closed month's realEom). 0 if no months closed yet. */
  currentMrr: number;
  /** Dec 2026 EOM target (or whatever the FY-end target is). */
  targetMrr: number;
  /** Localized FY-end label, e.g. "Dec 2026" / "Dic 2026". */
  targetMonthLabel: string;
  /** Months remaining (rows not yet locked). */
  monthsLeft: number;
  /** % ahead/behind the linear-pace expectation. Positive = ahead. */
  aheadPct: number;
  /** Bucketed status — drives the pill color/copy. */
  trackStatus: TrackStatus;
  /** Last closed row reference (for the "last closed" line). null if no rows
   *  are locked yet — the line collapses gracefully. */
  lastClosed: LastClosedLike | null;
  /** Human-readable label for the last-closed month, e.g. "Apr 2026". */
  lastClosedLabel?: string;
  /** "it" | "en" — drives all user-facing copy. */
  lang: "it" | "en";
  /** Money formatter (eur / fmtMoney). Receives a raw number, returns €-prefixed
   *  string. Caller controls MRR vs ARR multiplication. */
  fmtMoney: (n: number) => string;
}

export default function HeroKpi({
  currentMrr,
  targetMrr,
  targetMonthLabel,
  monthsLeft,
  aheadPct,
  trackStatus,
  lastClosed,
  lastClosedLabel,
  lang,
  fmtMoney,
}: HeroKpiProps) {
  const gap = Math.max(0, targetMrr - currentMrr);
  const pct = targetMrr > 0 ? Math.min(100, Math.max(0, (currentMrr / targetMrr) * 100)) : 0;
  const paceNeeded = monthsLeft > 0 ? gap / monthsLeft : 0;

  // Localized copy. Kept inline (rather than a t() helper) so the
  // structure of each sentence is obvious in one place.
  const t = {
    targetLabel: lang === "it" ? `Target ${targetMonthLabel}` : `${targetMonthLabel} target`,
    statusOn:     lang === "it" ? "On target" : "On target",
    statusAhead:  lang === "it" ? `Avanti ${aheadPct.toFixed(0)}%`     : `Ahead ${aheadPct.toFixed(0)}%`,
    statusBehind: lang === "it" ? `Indietro ${Math.abs(aheadPct).toFixed(0)}%` : `Behind ${Math.abs(aheadPct).toFixed(0)}%`,
    arrow: "→",
    pctSuffix: lang === "it" ? "% raggiunto" : "% to target",
    gapText: lang === "it"
      ? `Mancano ${fmtMoney(gap)} su ${monthsLeft} ${monthsLeft === 1 ? "mese" : "mesi"}`
      : `${fmtMoney(gap)} to go over ${monthsLeft} ${monthsLeft === 1 ? "month" : "months"}`,
    paceText: lang === "it"
      ? `Serve ${fmtMoney(paceNeeded)}/mese`
      : `Need ${fmtMoney(paceNeeded)}/mo`,
    lastClosedLabel: lang === "it" ? "Ultimo chiuso" : "Last closed",
    sep: " · ",
  };

  const statusLabel =
    trackStatus === "on" ? t.statusOn
    : trackStatus === "ahead" ? t.statusAhead
    : t.statusBehind;

  return (
    <Card padding="comfortable" elevation="raised" className="hero-kpi">
      <div className="hero-kpi__head">
        <span className="hero-kpi__target-label">{t.targetLabel}</span>
        <span className={`status-pill status-pill--${trackStatus}`}>
          <span className="status-dot" />
          {statusLabel}
        </span>
      </div>

      <div className="hero-kpi__numbers">
        <span className="hero-kpi__current">{fmtMoney(currentMrr)}</span>
        <span className="hero-kpi__arrow" aria-hidden="true">{t.arrow}</span>
        <span className="hero-kpi__target">{fmtMoney(targetMrr)}</span>
      </div>

      <div
        className="hero-kpi__progress"
        role="progressbar"
        aria-valuenow={Math.round(pct)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t.targetLabel}
      >
        <div
          className={`hero-kpi__progress-fill hero-kpi__progress-fill--${trackStatus}`}
          style={{ width: `${pct.toFixed(1)}%` }}
        />
        <span className="hero-kpi__progress-label">{pct.toFixed(0)}{t.pctSuffix}</span>
      </div>

      <div className="hero-kpi__caption">
        {monthsLeft > 0 ? (
          <>
            <span>{t.gapText}</span>
            <span className="hero-kpi__sep" aria-hidden="true">{t.sep}</span>
            <span className="hero-kpi__pace">{t.paceText}</span>
          </>
        ) : (
          // No months left → the FY is over; show only the gap (if any).
          <span>{t.gapText}</span>
        )}
        {lastClosed && lastClosedLabel ? (
          <>
            <span className="hero-kpi__sep" aria-hidden="true">{t.sep}</span>
            <span className="hero-kpi__last-closed">
              {t.lastClosedLabel}: {lastClosedLabel} · {fmtMoney(lastClosed.realEom)}
            </span>
          </>
        ) : null}
      </div>
    </Card>
  );
}
